// frontend/src/hooks/useSupplierMutations.js
//
// Bulk write paths for the Suppliers grid. A supplier update can trigger a
// currency-change confirmation gate (DefaultCurrency edit reinterpreting linked
// items' costs). The endpoint returns 200 with `requires_confirmation: true`
// BEFORE saving — the page surfaces the CurrencyChangeConfirmModal, then retries
// with the modal's `confirm` body fragment ({ currency_change_strategy } for
// this reinterpret gate) spread into the body.

import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';


/**
 * Bulk-update Supplier rows. A currency change reinterprets the linked
 * items' costs, so the write changes items too.
 *
 * Variables:
 *   - items       — `[{ id, fields }, ...]`
 *   - confirm?    — the modal's confirmation body fragment, spread into the
 *                   body on the retry after a `requires_confirmation` gate.
 */
export function useSupplierBulkUpdate() {
  return useApiMutation({
    mutationFn: ({ items, confirm }) => {
      const body = { items, ...(confirm || {}) };
      return apiJson.post('/api/suppliers/bulk-update/', body);
    },
    changes: [DATA.suppliers, DATA.items],
    edits: ['/api/suppliers/'],
  });
}


/**
 * Create a single supplier (for the Create supplier dialog).
 */
export function useCreateSupplier() {
  return useApiMutation({
    mutationFn: (payload) => apiJson.post('/api/suppliers/', payload),
    changes: [DATA.suppliers],
  });
}


/**
 * Delete the suppliers a grid selection names.
 *
 * Variables:
 *   - payload    — the selection body fragment from `readGridSelection`:
 *                  `{ids}` for individually ticked rows, or
 *                  `{select_all: true, exclude_ids}` for the header checkbox.
 *   - search?    — the grid's active search text. Rides in the QUERY STRING,
 *                  not the body, so the server resolves a select-all through
 *                  the same SearchFilter that produced the list the merchant
 *                  was looking at. Omit it and select-all means the whole
 *                  store, which is a very different delete.
 *   - confirmed? — see the gate below.
 *
 * **Two-step by design.** Without `confirmed` the endpoint deletes NOTHING and
 * returns `{requires_confirmation, suppliers, impact}`. Nothing pointing at
 * Supplier is PROTECT — purchase orders, PO recommendations and catalog links
 * are all CASCADE — so a supplier delete destroys its order history in the
 * same statement, and the write names those kinds too. `impact` is keyed by
 * Django model label; the page renders each as a translated noun so the
 * merchant reads what actually dies before agreeing to it.
 *
 * On success: `{deleted, cascaded}` — `deleted` counts SUPPLIERS, `cascaded`
 * the rows that went with them, keyed the same way.
 */
export function useSupplierBulkDelete() {
  return useApiMutation({
    mutationFn: ({ payload, search, confirmed }) => {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      return apiJson.delete(
        `/api/suppliers/bulk-delete/${query}`,
        { ...payload, ...(confirmed ? { confirmed: true } : {}) },
      );
    },
    changes: [DATA.suppliers, DATA.supplierLinks, DATA.purchaseOrders, DATA.recommendations],
  });
}
